import React, { useCallback, useRef } from 'react';
import { useTranslations } from '@unisporkal/localization';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
  CheckRoundedIcon,
  InfoRoundedIcon,
  Tooltip,
  TooltipPlacementEnum,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import { ExpandableBuyCard } from '../../../molecules/ExpandableBuyCard';
import { CaretText } from '../../../molecules/CaretText';
import { IncludedSizesTable } from '../../IncludedSizesHeader/IncludedSizesTable';
import useAcquisitionModal from '../../../../hooks/useAcquisitionModal';
import {
  trackClickViewLicenseDetailsCreativeAlc,
  trackClickViewLicenseDetailsEditorialAlc,
  trackIncludedSizesTooltipShown,
} from '../../../../support/ctaTracking';
import {
  SINGLE_IMAGE_CREATIVE_LICENSE_MODAL_ID,
  SINGLE_IMAGE_EDITORIAL_LICENSE_MODAL_ID,
} from '../../../../constants/modalConstants';
import { type AcquisitionModalState } from '../../../../types/acquisitionModalState';
import { type IncludedSize } from '../../../../../types/isomorphic';
import SizeOptionsSelector from './SizeOptionsSelector';
import { type SizeOption } from '../../../../types/sizeOption';
import styles from './ALCBuyCard.module.scss';

export type AlcAssetType = 'image' | 'video';

export type ALCBuyCardProps = {
  sizeOptions: SizeOption[];
  selectedSize?: string;
  onSelectSize?: (name: string) => void;
  assetType: AlcAssetType;
  includedSizes: IncludedSize[];
  includedSizesTranslationKey: string;
  isEditorial?: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  onExpand: () => void;
  onSelect: () => void;
};

const TITLE_TRANSLATION_KEY: Record<AlcAssetType, string> = {
  image: 'just_this_image',
  video: 'just_this_video',
};

const VIEW_LICENSE_DETAILS_TRANSLATION_KEY = 'view_license_details';

const ALCBuyCard = ({
  sizeOptions,
  selectedSize,
  onSelectSize,
  assetType,
  includedSizes,
  includedSizesTranslationKey,
  isEditorial,
  isExpanded,
  isSelected,
  onExpand,
  onSelect,
}: ALCBuyCardProps) => {
  const t = useTranslations();
  const { setModal } = useAcquisitionModal() as AcquisitionModalState;
  const hasTrackedTooltipShown = useRef(false);
  const includedSizesText = t(includedSizesTranslationKey);
  const selectedOption =
    sizeOptions.find(({ name }) => name === selectedSize) ?? sizeOptions[0];
  const hasSizeChoice = sizeOptions.length > 1;

  const handleIncludedSizesTooltipShown = useCallback(() => {
    if (hasTrackedTooltipShown.current) return;
    hasTrackedTooltipShown.current = true;
    trackIncludedSizesTooltipShown();
  }, []);

  const handleLicenseDetailsClick = () => {
    if (isEditorial) {
      trackClickViewLicenseDetailsEditorialAlc(
        VIEW_LICENSE_DETAILS_TRANSLATION_KEY
      );
      setModal(SINGLE_IMAGE_EDITORIAL_LICENSE_MODAL_ID);
    } else {
      trackClickViewLicenseDetailsCreativeAlc(
        VIEW_LICENSE_DETAILS_TRANSLATION_KEY
      );
      setModal(SINGLE_IMAGE_CREATIVE_LICENSE_MODAL_ID);
    }
  };

  return (
    <ExpandableBuyCard
      title={t(TITLE_TRANSLATION_KEY[assetType])}
      price={selectedOption?.price}
      isExpanded={isExpanded}
      isSelected={isSelected}
      onExpand={onExpand}
      onSelect={onSelect}
      data-testid="alc-buy-card"
    >
      {hasSizeChoice && selectedOption ? (
        <SizeOptionsSelector
          sizeOptions={sizeOptions}
          selectedOption={selectedOption}
          onSelectSize={(name) => onSelectSize?.(name)}
        />
      ) : (
        <div className={styles.includesAllSizes}>
          <CheckRoundedIcon
            className={styles.checkMark}
            size="16px"
          />
          <span className={styles.includedSizesText}>
            <Typography
              variant={TypographyVariant.body.small}
              component={TypographyComponentEnum.SPAN}
            >
              {includedSizesText}
            </Typography>
            <Tooltip
              placement={TooltipPlacementEnum.LEFT}
              content={
                <IncludedSizesTable
                  sizes={includedSizes}
                  ariaLabel={includedSizesText}
                  isVideo={assetType === 'video'}
                />
              }
              className={styles.includedSizesTooltip}
              onOpenChange={(open) => {
                if (open) handleIncludedSizesTooltipShown();
              }}
            >
              <InfoRoundedIcon
                className={styles.infoIcon}
                size="16px"
                data-testid="alc-buy-card-included-sizes-tooltip-trigger"
              />
            </Tooltip>
          </span>
        </div>
      )}

      <Button
        variant={ButtonVariantEnum.LINK}
        size={ButtonSizeEnum.EXTRA_SMALL}
        data-testid="alc-buy-card-view-license-details"
        onClick={handleLicenseDetailsClick}
      >
        <CaretText>{t(VIEW_LICENSE_DETAILS_TRANSLATION_KEY)}</CaretText>
      </Button>
    </ExpandableBuyCard>
  );
};

export default ALCBuyCard;
